import { Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useUpdateBrandSettingsMutation } from "redux/settings/settingsSlice";
import ImageVideoPicker from "components/image-video-picker";
import { UTILS } from "utils";
import { errorHandler } from "api/api/errorHandler";
import { postDataAsFormData } from "api/api/axiosSetup";
import { SettingsAction } from "redux/actions";
import { LoaderAction } from "redux/actions";
import AppInput from "components/ui/AppInput";
import AppTextArea from "components/ui/AppTextArea";
import AppSwitch from "components/ui/AppSwitch";
import AppButton from "components/ui/AppButton";

const validateFields = ({ key, value, rules = [] }) => {
  const fieldError = rules.find((rule) => {
    if (
      rule.required &&
      (value === "" || value === undefined || value === null)
    ) {
      return true;
    }
    if (
      rule.minLength &&
      typeof value === "string" &&
      value &&
      value.length < rule.minLength
    ) {
      return true;
    }
    if (
      rule.maxLength &&
      typeof value === "string" &&
      value &&
      value.length > rule.maxLength
    ) {
      return true;
    }
    if (
      rule.pattern &&
      typeof value === "string" &&
      value &&
      !rule.pattern.test(value)
    ) {
      return true;
    }
    return false;
  });

  return fieldError ? { [key]: fieldError.message } : {};
};

const bannerUrlRules = [
  {
    required: true,
    message: "Banner URL is required",
  },
];

const bannerTextRules = [
  {
    maxLength: 50,
    message: "Banner Title should not exceed 100 characters",
  },
  {
    minLength: 3,
    message: "Banner Title should be at least 3 characters long",
  },
];

const bannerDescriptionRules = [
  {
    maxLength: 100,
    message: "Banner Description should not exceed 100 characters",
  },
  {
    minLength: 10,
    message: "Banner Description should be at least 10 characters long",
  },
];

const validationKeys = ["bannerText", "bannerDescription", "bannerUrl"];

const PromotionalBanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { settings } = useSelector((state) => state?.SettingsReducer);
  const [updateBrandSettings] = useUpdateBrandSettingsMutation();

  const { isLoading } = useSelector((state) => ({
    isLoading: state.LoaderReducer.isLoading,
  }));

  const [errors, setErrors] = useState({});
  const [state, setState] = useState({
    bannerText: "",
    bannerDescription: "",
    showBanner: false,
    bannerUrl: "",
    showOnStore: [],
    schedule: null,
  });

  const rulesForKey = (key) => {
    if (key === "bannerText") return bannerTextRules;
    if (key === "bannerDescription") return bannerDescriptionRules;
    if (key === "bannerUrl") return bannerUrlRules;
    return [];
  };

  const handleChange = (key, value, rules, customOnChange) => {
    const nextErrors = validateFields({ key, value, rules });
    if (Object.keys(nextErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...nextErrors }));
    } else {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
    if (customOnChange) {
      customOnChange(value, nextErrors, setState, setErrors);
    } else {
      setState((prev) => ({ ...prev, [key]: value }));
    }
  };

  const validateForm = () => {
    const acc = validationKeys.reduce((accum, key) => {
      const error = validateFields({
        key,
        value: state?.[key],
        rules: rulesForKey(key),
      });
      return { ...accum, ...error };
    }, {});

    const banner = state?.bannerUrl;
    const hasUploadError =
      banner &&
      typeof banner === "object" &&
      !Array.isArray(banner) &&
      (banner.status === "error" || banner?.error?.message);
    if (hasUploadError) {
      acc.bannerUrl =
        banner?.error?.message || acc.bannerUrl || "Banner URL is required";
    }

    return acc;
  };

  const handleMediaUpload = async (file) => {
    try {
      const resp = await postDataAsFormData(
        "fms/upload-media-files",
        { media: file, preventCache: true },
        ["media"]
      );
      return resp?.data;
    } catch (error) {
      console.log(error);
    }
  };

  const handleBannerFileChange = (fileList) => {
    const file = Array.isArray(fileList) ? fileList?.[0] : fileList;
    const fieldErrors = validateFields({
      key: "bannerUrl",
      value: file,
      rules: bannerUrlRules,
    });
    const uploadError = file?.error?.message;
    setState((prev) => ({ ...prev, bannerUrl: file || "" }));
    setErrors((prev) => ({
      ...prev,
      bannerUrl: fieldErrors?.bannerUrl
        ? fieldErrors.bannerUrl
        : uploadError || "",
    }));
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    try {
      dispatch(LoaderAction.setLoading(true));
      const obj = {
        brandId: settings?.brandId,
        language: "EN",
      };
      let imageUrl = state?.bannerUrl;
      if (imageUrl && imageUrl?.originFileObj) {
        imageUrl = await handleMediaUpload(imageUrl?.originFileObj);
      }

      const { data } = await updateBrandSettings({
        promotionalBannerConfig: {
          ...state,
          bannerType: "image",
          bannerUrl: imageUrl,
        },
        ...obj,
      });
      if (data?.success === 1) {
        dispatch(
          SettingsAction.setSettings({
            ...data?.data?.setting,
            brandId: settings?.brandId,
          })
        );
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    }
  };

  useEffect(() => {
    if (settings?.promotionalBannerConfig) {
      setState((prev) => ({
        ...prev,
        ...(settings?.promotionalBannerConfig || {}),
      }));
    }
  }, [settings?.promotionalBannerConfig]);

  const bannerTextError = errors?.bannerText ? t(errors.bannerText) : null;
  const bannerDescriptionError = errors?.bannerDescription
    ? t(errors.bannerDescription)
    : null;

  return (
    <Spin spinning={isLoading}>
      <form>
        <div className="mt-1 grid grid-flow-row-dense gap-x-3 gap-y-2 md:grid-cols-2">
          <div className="col-span-2">
            <AppInput
              label={t("Banner Title")}
              value={state.bannerText}
              placeholder={t("Enter Banner Title")}
              showCount
              maxLength={50}
              error={bannerTextError}
              onChange={(e) =>
                handleChange(
                  "bannerText",
                  e.target.value,
                  bannerTextRules,
                  null
                )
              }
            />
          </div>

          <div className="col-span-2">
            <AppTextArea
              label={t("Banner Description")}
              value={state.bannerDescription}
              placeholder={t("Enter Banner Description")}
              showCount
              maxLength={100}
              error={bannerDescriptionError}
              onChange={(e) =>
                handleChange(
                  "bannerDescription",
                  e.target.value,
                  bannerDescriptionRules,
                  null
                )
              }
            />
          </div>

          <div className="col-span-2 flex flex-col gap-2">
            <div className="self-start">
              <AppSwitch
                checked={state.showBanner}
                label={t("Is Active")}
                onChange={(checked) =>
                  handleChange("showBanner", checked, [], null)
                }
              />
            </div>
            <ImageVideoPicker
              label={
                <span className="inline-flex flex-wrap items-baseline gap-1">
                  {t("Upload Media")}
                  <span className="text-red-500">*</span>
                </span>
              }
              info={{
                text: "Promotional banner upload hint",
                props: {
                  maxSizeMB: 2,
                  recommended: "150×150px",
                  maxSizeMBVideo: 10,
                },
              }}
              value={state.bannerUrl}
              validationRules={{
                image: {
                  maxSizeMB: 2,
                },
              }}
              onChange={handleBannerFileChange}
            />
          </div>
        </div>

        <div className="mb-2 mt-4">
          <AppButton
            type="button"
            variant="primary"
            size="lg"
            loading={isLoading}
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {t("Update Promotional Banner")}
          </AppButton>
        </div>
      </form>
    </Spin>
  );
};

export default PromotionalBanner;
